import {Box, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {t} from 'i18next';
import './MultipageRedaction.scss';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../shared/types';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {InfoOutline} from '@mui/icons-material';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {useGetRedactionTags} from '../../../../hooks/document/useDocument';

type MultipageRedactionProps = {
  onClose: () => void;
  saveMultiPageRedaction: (value: number[]) => void;
  selectedValue: ISelectItem | null;
  setSelectedValue: React.Dispatch<React.SetStateAction<ISelectItem | null>>;
  totalPages: number;
};
type PagePart =
  | {type: 'single'; value: number}
  | {type: 'range'; start: number; end: number}
  | {type: 'invalid'};
const enMultipageRedaction = 'docView.multiPageRedaction';
const redactionMethodOptions: ISelectItem[] = [
  {id: '1', name: 'Pages that will be redacted'},
  {id: '2', name: 'Pages that will not be redacted'},
];
const toCleanRegex = /\s+/g;

const MultipageRedaction: React.FC<MultipageRedactionProps> = ({
  onClose,
  saveMultiPageRedaction,
  selectedValue,
  setSelectedValue,
  totalPages,
}) => {
  const [pages, setPages] = useState<string>('');
  const [redactionMethod, setRedactionMethod] = useState<ISelectItem | null>(
    redactionMethodOptions[0]
  );
  const [pagesError, setPagesError] = useState<string | boolean>('');
  const [redactionOptions, setRedactionOptions] = useState<ISelectItem[]>([]);
  const {data} = useGetRedactionTags(true);
  useEffect(() => {
    setRedactionOptions(
      data?.redactionTagDetails.map((tag) => {
        return {
          id: tag.redactionID,
          name: tag.name,
          isDefault: tag.isSelected,
        } as ISelectItem;
      }) ?? []
    );
  }, [data]);
  useEffect(() => {
    const defaultOption = redactionOptions.find((item) => item.isDefault);
    if (defaultOption) {
      setSelectedValue(defaultOption);
    }
  }, [redactionOptions, setSelectedValue]);

  const parsePagePart = (part: string): PagePart => {
    if (!part) return {type: 'invalid'};

    // single number
    if (/^\d+$/.test(part)) {
      const n = parseInt(part, 10);
      return {type: 'single', value: n};
    }

    // range
    if (/^\d+-\d+$/.test(part)) {
      const [s, e] = part.split('-');
      return {
        type: 'range',
        start: parseInt(s, 10),
        end: parseInt(e, 10),
      };
    }

    return {type: 'invalid'};
  };

  useEffect(() => {
    if (pages) {
      setPagesError(validatePages(pages, totalPages));
    }
  }, [pages, redactionMethod]);

  const numRange = (start: number, end: number): number[] => {
    return Array.from({length: end - start + 1}, (_, index) => start + index);
  };

  const validatePages = (input: string, maxPage: number): boolean | string => {
    if (!input?.trim()) return t(`${enMultipageRedaction}.pagesError`);

    if (input?.trim().length >= 255) {
      return t(`${enMultipageRedaction}.pagesErrorMaxChars`);
    }

    const clean = input.replace(toCleanRegex, '');
    const parts = clean.split(',');
    const isRedactionInclusive =
      (redactionMethod?.id && Number(redactionMethod.id) === 1) || false;
    let selections: number[] = [];

    for (const part of parts) {
      const parsed = parsePagePart(part);

      if (parsed.type === 'invalid')
        return t(`${enMultipageRedaction}.pagesError`);

      if (parsed.type === 'single') {
        if (parsed.value <= 0 || parsed.value > maxPage) {
          return t(`${enMultipageRedaction}.pagesError`);
        } else {
          selections.push(parsed.value);
        }
      }

      if (parsed.type === 'range') {
        const {start, end} = parsed;

        if (
          start <= 0 ||
          end <= 0 ||
          start >= end ||
          start > maxPage ||
          end > maxPage
        ) {
          return t(`${enMultipageRedaction}.pagesError`);
        } else {
          selections.push(...numRange(start, end));
        }
      }
    }

    // Check for edge cases (Overlaps)
    selections = [...new Set(selections)]; //Remove duplicates
    selections = selections.sort((a, b) => a - b); //sort
    const selectLength: number = selections.length;

    if (selectLength <= 0) return t(`${enMultipageRedaction}.pagesErrorEmpty`);
    if (selectLength === maxPage)
      return isRedactionInclusive
        ? ''
        : t(`${enMultipageRedaction}.pagesErrorEmpty`);

    return false;
  };

  /**
   *
   * @param input user entered pages e.g. "1,3-5,8"
   * @param getRemaining if true → returns remaining pages
   * @param maxPage required when getRemaining=true
   */
  const getUniquePages = (
    input: string,
    getRemaining: boolean,
    maxPage: number
  ): number[] => {
    if (!input?.trim())
      return getRemaining && maxPage ? getAllPages(maxPage) : [];

    // --- Step 1: extract selected pages ---
    const clean = input.replace(toCleanRegex, '');
    const parts = clean.split(',');
    const pages = new Set<number>();

    for (const part of parts) {
      const parsed = parsePagePart(part);

      if (parsed.type === 'single') {
        pages.add(parsed.value);
      }

      if (parsed.type === 'range') {
        for (let p = parsed.start; p <= parsed.end; p++) {
          pages.add(p);
        }
      }
    }

    const selected = Array.from(pages).sort((a, b) => a - b);

    // --- Step 2: If user wants remaining pages ---
    if (getRemaining) {
      return getAllPages(maxPage).filter((p) => !pages.has(p));
    }

    return selected;
  };

  // Helper to generate 1..N
  const getAllPages = (maxPage: number): number[] =>
    Array.from({length: maxPage}, (_, i) => i + 1);

  const handleChangePages = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = event.target;
    setPages(value);
    setPagesError(validatePages(value, totalPages));
  };
  const handleBlurPages = (e: any) => {
    setPagesError(validatePages(e.target.value, totalPages));
  };
  const isSaveDisabled = !pages || pagesError || !redactionMethod;

  const headerTitleElement = (
    <Box className="modal-title-box" data-testid="reduction-modal-title">
      <Typography
        className="typo-text headline3_size modal-title"
        component="span"
        data-testid={`multipage-redaction-title`}
      >
        {t(`${enMultipageRedaction}.title`)}&nbsp;
      </Typography>
      <Typography
        component="span"
        data-testid={`multipage-redaction-title-tooltip-text`}
      >
        <ArrTooltipWrapper
          title={t(`${enMultipageRedaction}.title`)}
          trigger={
            <InfoOutline
              fontSize="medium"
              className="info-icon"
              data-testid="modal-title-info-icon"
            />
          }
        >
          {t(`${enMultipageRedaction}.titleTooltip`)}
        </ArrTooltipWrapper>
      </Typography>
    </Box>
  );
  const handleSave = () => {
    handleCloseModal();
    saveMultiPageRedaction(
      getUniquePages(pages, redactionMethod?.id === '2', totalPages)
    );
  };
  const handleCloseModal = () => {
    setRedactionOptions([]);
    onClose();
  };

  return (
    <ArrModal
      showModal={true}
      onClose={handleCloseModal}
      headerTitleElement={headerTitleElement}
      description={t(`${enMultipageRedaction}.desc`)}
      showClose={true}
      className="redaction-modal"
      dataTestId="multipage-redaction-modal"
      buttons={[
        {
          id: '1',
          label: t('common.cancel'),
          onClick: handleCloseModal,
          variant: 'secondary',
          dataTestId: 'mrm-cancel-btn',
        },
        {
          id: '2',
          label: t('common.save'),
          onClick: handleSave,
          variant: 'primary',
          disabled: !!isSaveDisabled,
          dataTestId: 'mrm-save-btn',
        },
      ]}
    >
      <Box className="redaction-tag-box">
        {redactionOptions?.length > 0 && (
          <ArrSingleSelectDropdown
            dropdownLabel={t(`${enMultipageRedaction}.redactionTag`)}
            dropdownItems={redactionOptions}
            selectedValues={selectedValue}
            onSelectionChange={setSelectedValue}
            formControlRestProps={{required: true}}
            dataTestId="asd-select-redaction-tag"
          />
        )}
      </Box>
      <Box className="input-with-tooltip-box">
        <ArrSingleSelectDropdown
          dropdownLabel={t(`${enMultipageRedaction}.redactionMethod`)}
          dropdownItems={redactionMethodOptions}
          selectedValues={redactionMethod}
          onSelectionChange={setRedactionMethod}
          formControlRestProps={{required: true}}
          dataTestId="asd-select-redaction-method"
        />
        <ArrTooltipWrapper
          trigger={
            <InfoOutline
              fontSize="medium"
              className="info-icon"
              data-testid="redaction-method-info-icon"
            />
          }
        >
          <Typography
            className="typo-text label3_size"
            data-testid="redaction-method-tooltip-title"
          >
            {t(`${enMultipageRedaction}.redactionMethod`)}
          </Typography>
          <Typography
            className="typo-text body4_size"
            data-testid="redaction-method-tooltip"
          >
            {t(`${enMultipageRedaction}.redactionMethodTooltip`)}
          </Typography>
        </ArrTooltipWrapper>
      </Box>
      <Box className="input-with-tooltip-box">
        <ArrTextField
          id="pages-input"
          state={pagesError ? 'error' : 'enabled'}
          label={t(`${enMultipageRedaction}.pages`)}
          required
          name="pages"
          aria-label={t(`${enMultipageRedaction}.pages`)}
          value={pages}
          onChange={handleChangePages}
          onBlur={handleBlurPages}
          dataTestId="multipage-redaction-pages-input"
          supportText={pagesError ? (pagesError as string) : ''}
          showErrorIcon={Boolean(pagesError)}
          aria-invalid={!!pagesError}
          placeholder={t(`${enMultipageRedaction}.pagesPlaceholder`)}
        />
        <ArrTooltipWrapper
          trigger={
            <InfoOutline
              fontSize="medium"
              className="info-icon"
              data-testid="info-icon"
            />
          }
        >
          <Typography
            className="typo-text label3_size"
            data-testid="pages-tooltip-title"
          >
            {t(`${enMultipageRedaction}.pagesTooltipTitle`)}
          </Typography>
          <Typography
            className="typo-text body4_size"
            data-testid="pages-tooltip"
          >
            {t(`${enMultipageRedaction}.pagesTooltip`)}
          </Typography>
        </ArrTooltipWrapper>
      </Box>
    </ArrModal>
  );
};

export default MultipageRedaction;
